// Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    console.log("HMR");
  });
}

import "./css/styles.css";

/* Fonts */
import "@fontsource/open-sauce-sans/400.css";
import "@fontsource/open-sauce-sans/500.css";
import "@fontsource/open-sauce-sans/600.css";
import "@fontsource/open-sauce-sans/700.css";
import "@fontsource/old-standard-tt/400.css";
import "@fontsource/old-standard-tt/400-italic.css";
import "@fontsource-variable/karla/wght.css";

// JS
//import "./js/index.js"

import Alpine from "alpinejs";
import collapse from "@alpinejs/collapse";
import { gsap } from "gsap";
import Swiper from "swiper";
import { Navigation, Scrollbar } from "swiper/modules";

window.Alpine = Alpine;
Alpine.plugin(collapse);

Alpine.data("carousel", () => {
  let swiper = null;

  return {
    init(options = {}) {
      const {
        slidesPerView = 1,
        spaceBetween = 0,
        breakpoints = {},
      } = options ?? {};

      if (!this.$refs.carousel) return;

      swiper = new Swiper(this.$refs.carousel, {
        modules: [Navigation, Scrollbar],
        slidesPerView: slidesPerView,
        spaceBetween: spaceBetween,
        navigation: {
          nextEl: this.$refs.nextBtn,
          prevEl: this.$refs.prevBtn,
        },
        breakpoints,
        loop: true,
        autoHeight: true,
        speed: 600,
      });
    },
    next() {
      swiper.slideNext();
    },
    prev() {
      swiper.slidePrev();
    },
    destroy() {
      if (swiper) {
        swiper.destroy(true, true);
        swiper = null;
      }
    },
  };
});

const HERO_ROTATING_TEXT = Object.freeze({
  textCharHeight: 1.5,
  textCharWidth: 1.1,
  letterSpacing: 1,
  textCharRotation: -10,
  pathTiltDegrees: -10,
  pathDuration: 20,
  animationSpeed: 0.7,
});

const createRotatingTextOverlay = ({ getTargetBounds }) => ({
  overlayRafId: null,
  overlayCanvases: [],
  overlayContexts: [],
  canvasDisplayWidth: 0,
  canvasDisplayHeight: 0,
  lastFrameTime: null,
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  init() {
    this.$nextTick(() => {
      this.updateOverlayBounds();
      this.initCanvas();
      this.startCanvasLoop();
    });
  },
  initCanvas() {
    const canvasRefs = [
      this.$refs.overlayCanvas,
      this.$refs.overlayCanvasLeft,
      this.$refs.overlayCanvasRight,
      this.$refs.overlayCanvasTop,
      this.$refs.overlayCanvasBottom,
    ]
      .filter(Boolean)
      .filter((canvas) => canvas.clientWidth > 0 && canvas.clientHeight > 0);

    if (!canvasRefs.length) return;

    this.overlayCanvases = canvasRefs;
    this.overlayContexts = canvasRefs.map((canvas) => canvas.getContext("2d"));
    this.syncCanvasSize();
  },
  syncCanvasSize() {
    if (!this.overlayCanvases.length || !this.overlayContexts.length) return;

    const firstCanvas = this.overlayCanvases[0];
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = Math.max(1, firstCanvas.clientWidth);
    const displayHeight = Math.max(1, firstCanvas.clientHeight);

    this.canvasDisplayWidth = displayWidth;
    this.canvasDisplayHeight = displayHeight;

    this.overlayCanvases.forEach((canvas, index) => {
      const ctx = this.overlayContexts[index];
      if (!ctx) return;

      canvas.width = Math.floor(displayWidth * dpr);
      canvas.height = Math.floor(displayHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    });
  },
  startCanvasLoop() {
    let textLoopOffset = 0;
    const renderBounds = {
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
    };

    const loop = () => {
      const now = performance.now();
      const elapsedMs = this.lastFrameTime ? now - this.lastFrameTime : 16.67;
      this.lastFrameTime = now;
      const elapsedSeconds = elapsedMs / 1000;
      const pathDuration = Math.max(0.1, HERO_ROTATING_TEXT.pathDuration);
      const animationSpeed = Math.max(0, HERO_ROTATING_TEXT.animationSpeed);
      const radiansPerSecond = ((Math.PI * 2) / pathDuration) * animationSpeed;
      textLoopOffset += radiansPerSecond * elapsedSeconds;
      const easing = 0.08;
      renderBounds.x += (this.x - renderBounds.x) * easing;
      renderBounds.y += (this.y - renderBounds.y) * easing;
      renderBounds.width += (this.width - renderBounds.width) * easing;
      renderBounds.height += (this.height - renderBounds.height) * easing;

      this.drawOverlayFrame(textLoopOffset, renderBounds);
      this.overlayRafId = window.requestAnimationFrame(loop);
    };

    if (this.overlayRafId) window.cancelAnimationFrame(this.overlayRafId);
    this.overlayRafId = window.requestAnimationFrame(loop);
  },
  drawOverlayFrame(textLoopOffset, renderBounds) {
    if (!this.overlayContexts.length || !this.overlayCanvases.length) return;

    const firstCanvas = this.overlayCanvases[0];
    const width = firstCanvas.clientWidth;
    const height = firstCanvas.clientHeight;

    if (!width || !height) return;
    if (
      width !== this.canvasDisplayWidth ||
      height !== this.canvasDisplayHeight
    ) {
      this.syncCanvasSize();
    }

    this.overlayContexts.forEach((ctx) => ctx?.clearRect(0, 0, width, height));

    const phrase = "to gather together to gather together";
    const fontSize = Math.max(
      12,
      Math.min(renderBounds.width, renderBounds.height) * 0.06,
    );
    const textCharHeight = Math.max(0.1, HERO_ROTATING_TEXT.textCharHeight);
    const textCharWidth = Math.max(0.1, HERO_ROTATING_TEXT.textCharWidth);
    const letterSpacing = Math.max(0.1, HERO_ROTATING_TEXT.letterSpacing);
    const textCharRotationRadians =
      (HERO_ROTATING_TEXT.textCharRotation * Math.PI) / 180;
    const charStep = ((Math.PI * 2) / phrase.length) * letterSpacing;
    const pathRotation = (HERO_ROTATING_TEXT.pathTiltDegrees * Math.PI) / 180;
    const cosRotation = Math.cos(pathRotation);
    const sinRotation = Math.sin(pathRotation);

    this.overlayContexts.forEach((ctx, index) => {
      if (!ctx) return;
      const canvas = this.overlayCanvases[index];
      const color = canvas
        ? window.getComputedStyle(canvas).color || "#000000"
        : "#000000";
      const contextBounds =
        typeof this.getCanvasRenderBounds === "function"
          ? this.getCanvasRenderBounds(canvas, renderBounds) || renderBounds
          : renderBounds;
      const cx = contextBounds.x + contextBounds.width / 2;
      const cy = contextBounds.y + contextBounds.height / 2;
      const rx = contextBounds.width * 0.47;
      const ry = contextBounds.height * 0.42;

      ctx.fillStyle = color;
      ctx.font = `italic 500 ${fontSize}px "Old Standard TT", serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      for (let i = 0; i < phrase.length; i += 1) {
        const angle = textLoopOffset + i * charStep;
        const ellipseX = rx * Math.cos(angle);
        const ellipseY = ry * Math.sin(angle);
        const x = cx + ellipseX * cosRotation - ellipseY * sinRotation;
        const y = cy + ellipseX * sinRotation + ellipseY * cosRotation;
        const tangentX =
          -rx * Math.sin(angle) * cosRotation -
          ry * Math.cos(angle) * sinRotation;
        const tangentY =
          -rx * Math.sin(angle) * sinRotation +
          ry * Math.cos(angle) * cosRotation;
        const tangentAngle = Math.atan2(tangentY, tangentX);
        // Text is drawn with its baseline on the x-axis. Offset by -90deg so
        // glyph verticals (like "I") follow the path tangent at 0deg.
        const charRotation =
          tangentAngle - Math.PI / 2 + textCharRotationRadians;

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(charRotation);
        ctx.scale(textCharWidth, textCharHeight);
        ctx.fillText(phrase[i], 0, 0);
        ctx.restore();
      }
    });
  },
  updateOverlayBounds() {
    const bounds = getTargetBounds.call(this);
    if (!bounds) return;

    this.x = bounds.x;
    this.y = bounds.y;
    this.width = bounds.width;
    this.height = bounds.height;
  },
  getOverlayStyle() {
    return "width: 100%; height: 100%; transform: translate3d(0px, 0px, 0); transform-origin: top left;";
  },
  destroy() {
    if (this.overlayRafId) window.cancelAnimationFrame(this.overlayRafId);
    this.lastFrameTime = null;
  },
});

Alpine.data("enhancedHero", () => ({
  ...createRotatingTextOverlay({
    getTargetBounds() {
      const containerRect = this.$el.getBoundingClientRect();
      const screenCenterX = window.innerWidth / 2;
      const centerXInContainer = screenCenterX - containerRect.left;
      const centeredPathWidth = Math.min(1440, containerRect.width);

      if (!this.isDesktopLayout()) {
        return {
          x: 0,
          y: 0,
          width: containerRect.width,
          height: containerRect.height,
        };
      }

      if (this.selectedSide === "left" || this.selectedSide === "right") {
        const sidePathWidth = Math.min(720, centeredPathWidth / 2);
        const sideOffset = centeredPathWidth / 4;
        const targetCenterX =
          centerXInContainer +
          (this.selectedSide === "left" ? -sideOffset : sideOffset);

        return {
          x: targetCenterX - sidePathWidth / 2,
          y: 0,
          width: sidePathWidth,
          height: containerRect.height,
        };
      }

      return {
        x: centerXInContainer - centeredPathWidth / 2,
        y: 0,
        width: centeredPathWidth,
        height: containerRect.height,
      };
    },
  }),
  selectedSide: null,
  /** True briefly after pointerdown in hero; blocks focusin from pinning a side on click vs Tab. */
  skipNextFocusinForSide: false,
  init() {
    this.selectedSide = null;
    this.skipNextFocusinForSide = false;
    this.$nextTick(() => {
      this.updateOverlayBounds();
      this.initCanvas();
      this.startCanvasLoop();
    });
  },
  onHeroPointerDownCapture() {
    this.skipNextFocusinForSide = true;
    window.setTimeout(() => {
      this.skipNextFocusinForSide = false;
    }, 0);
  },
  onPanelFocusIn(side, event) {
    const e = event;
    if (e?.sourceCapabilities?.firesTouchEvents) return;
    if (this.skipNextFocusinForSide) return;
    this.setSelectedSide(side);
  },
  setSelectedSide(side) {
    if (!this.isDesktopLayout()) {
      if (this.selectedSide !== null) this.selectedSide = null;
      this.updateOverlayBounds();
      return;
    }
    // Wide touch devices still match isDesktopLayout; never pin a side unless
    // the primary input is hover + fine pointer (avoids tap-to-select via focusin).
    if (!this.supportsPointerHoverInteraction()) {
      if (this.selectedSide !== null) {
        this.selectedSide = null;
        this.updateOverlayBounds();
      }
      return;
    }
    if (side !== "left" && side !== "right" && side !== null) return;
    if (this.selectedSide === side) return;
    this.selectedSide = side;
    this.updateOverlayBounds();
  },
  updateSideFromPointer(event) {
    if (!this.isDesktopLayout() || !this.supportsPointerHoverInteraction()) return;
    const { pointerType } = event;
    if (pointerType && pointerType !== "mouse") return;
    const rect = this.$el.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const side = localX < rect.width / 2 ? "left" : "right";
    this.setSelectedSide(side);
  },
  clearSelectedSide() {
    this.setSelectedSide(null);
  },
  getCanvasRenderBounds(canvas, defaultBounds) {
    if (!canvas || this.isDesktopLayout()) return defaultBounds;

    const halfHeight = defaultBounds.height / 2;
    if (canvas === this.$refs.overlayCanvasTop) {
      return {
        x: 0,
        y: 0,
        width: defaultBounds.width,
        height: halfHeight,
      };
    }

    if (canvas === this.$refs.overlayCanvasBottom) {
      return {
        x: 0,
        y: halfHeight,
        width: defaultBounds.width,
        height: halfHeight,
      };
    }

    return defaultBounds;
  },
  handleFocusOut() {
    if (!this.isDesktopLayout()) return;
    this.$nextTick(() => {
      if (!this.$el.contains(document.activeElement)) {
        this.clearSelectedSide();
      }
    });
  },
  isDesktopLayout() {
    return window.matchMedia("(min-width: 1024px)").matches;
  },
  /** True when the primary input is hover-capable with a fine pointer (typical desktop). */
  supportsPointerHoverInteraction() {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  },
  handleResize() {
    if (!this.isDesktopLayout() || !this.supportsPointerHoverInteraction()) {
      this.selectedSide = null;
    }
    this.updateOverlayBounds();
    this.initCanvas();
    this.syncCanvasSize();
  },
}));

Alpine.data("simpleHero", () => ({
  ...createRotatingTextOverlay({
    getTargetBounds() {
      const containerRect = this.$el.getBoundingClientRect();
      const screenCenterX = window.innerWidth / 2;
      const centerXInContainer = screenCenterX - containerRect.left;
      const centeredPathWidth = Math.min(1440, containerRect.width);
      return {
        x: centerXInContainer - centeredPathWidth / 2,
        y: 0,
        width: centeredPathWidth,
        height: containerRect.height,
      };
    },
  }),
  handleResize() {
    this.updateOverlayBounds();
    this.initCanvas();
    this.syncCanvasSize();
  },
}));

Alpine.data("mobileNav", () => ({
  panelStack: ["root"],
  panelOrder: {},
  init() {
    this.buildPanelOrder();
    this.$watch("menuOpen", (isOpen) => {
      if (!isOpen) this.resetPanels();
    });
  },
  buildPanelOrder() {
    this.panelOrder = {};
    const panels = this.$el.querySelectorAll("[data-nav-panel-id]");
    panels.forEach((panel, index) => {
      const panelId = panel.dataset.navPanelId;
      if (!panelId) return;
      this.panelOrder[panelId] = index;
    });
  },
  goToPanel(panelId) {
    if (!(panelId in this.panelOrder)) return;
    if (this.panelStack[this.panelStack.length - 1] === panelId) return;
    this.panelStack.push(panelId);
  },
  goBack() {
    if (this.panelStack.length <= 1) return;
    this.panelStack.pop();
  },
  resetPanels() {
    this.panelStack = ["root"];
  },
  get activePanelId() {
    return this.panelStack[this.panelStack.length - 1] || "root";
  },
  get activePanelIndex() {
    if (!(this.activePanelId in this.panelOrder)) return 0;
    return this.panelOrder[this.activePanelId];
  },
}));

Alpine.data("tickerTape", () => ({
  speed: -60,
  x: 0,
  wrap: null,
  tick: null,
  resizeHandler: null,
  clonedContent: null,
  init() {
    this.setupTrack();
  },
  setupTrack() {
    const track = this.$refs.track;
    const originalContent = this.$refs.content;
    if (!track || !originalContent) return;

    this.clonedContent?.remove();
    this.clonedContent = originalContent.cloneNode(true);
    this.clonedContent.setAttribute("data-ticker-clone", "true");
    track.appendChild(this.clonedContent);

    this.recalculateTrack();
    this.tick = () => {
      if (!this.wrap) return;
      const frameRatio = gsap.ticker.deltaRatio(60);
      this.x += (this.speed / 60) * frameRatio;
      this.x = this.wrap(this.x);
      gsap.set(track, { x: this.x });
    };
    gsap.ticker.add(this.tick);

    this.resizeHandler = () => {
      this.recalculateTrack();
    };
    window.addEventListener("resize", this.resizeHandler);
  },
  recalculateTrack() {
    const track = this.$refs.track;
    const originalContent = this.$refs.content;
    if (!track || !originalContent) return;

    const distance = originalContent.offsetWidth;
    if (!distance) {
      this.wrap = null;
      return;
    }

    this.wrap = gsap.utils.wrap(-distance, 0);
    this.x = this.wrap(this.x);
    gsap.set(track, { x: this.x });
  },
  destroy() {
    if (this.tick) gsap.ticker.remove(this.tick);
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    this.clonedContent?.remove();
    if (this.$refs.track) gsap.set(this.$refs.track, { clearProps: "x" });
  },
}));

Alpine.start();

window.exampleAlert = () =>
  alert("DDEV + Vite + Tailwind + Alpine are wired up!");
